import PropTypes from 'prop-types'
// material
import { alpha, styled } from '@material-ui/core/styles'

// ----------------------------------------------------------------------

// Clinical scales a Label can take its colour from (theme.palette.acmg /
// theme.palette.clinvar). Each tone carries { main, text, bg } for the active mode.
const SCALES = ['acmg', 'clinvar']

const RootStyle = styled('span')(({ theme, styleProps }) => {
  const isLight = theme.palette.mode === 'light'
  const { color, variant, tone, scale } = styleProps
  const clinical = tone ? theme.palette[scale]?.[tone] : null

  const styleFilled = (color) => ({
    color: theme.palette[color].contrastText,
    backgroundColor: theme.palette[color].main,
  })

  const styleOutlined = (color) => ({
    color: theme.palette[color][isLight ? 'dark' : 'light'],
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette[color].main}`,
  })

  const styleGhost = (color) => ({
    color: theme.palette[color][isLight ? 'dark' : 'light'],
    backgroundColor: alpha(theme.palette[color].main, 0.16),
  })

  // "text" — same dimensions as a chip, but no fill or border. Used by
  // the unified-view cockpit pass so colour comes through as bold text
  // while the box keeps the grid aligned. Warning uses the .dark shade
  // in light mode since the .main yellow loses contrast on white.
  const styleText = (color) => ({
    color: theme.palette[color][isLight ? 'dark' : 'light'],
    backgroundColor: 'transparent',
  })

  // Clinical tones: `text` is already the readable shade for the mode and
  // `bg` the tinted fill, so ghost never puts light text on a bright fill.
  const styleClinical = (token) => ({
    ...(variant === 'filled' && {
      color: theme.palette.getContrastText(token.main),
      backgroundColor: token.main,
    }),
    ...(variant === 'outlined' && {
      color: token.text,
      backgroundColor: 'transparent',
      border: `1px solid ${token.main}`,
    }),
    ...(variant === 'ghost' && { color: token.text, backgroundColor: token.bg }),
    ...(variant === 'text' && { color: token.text, backgroundColor: 'transparent' }),
  })

  const styleDefault = {
    ...(variant === 'outlined' && {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.grey[500_32]}`,
    }),
    ...(variant === 'ghost' && {
      color: isLight ? theme.palette.text.secondary : theme.palette.text.primary,
      backgroundColor: theme.palette.grey[500_16],
    }),
    ...(variant === 'text' && {
      color: theme.palette.text.secondary,
      backgroundColor: 'transparent',
    }),
  }

  let tonal = styleDefault
  if (clinical) tonal = styleClinical(clinical)
  else if (color !== 'default' && theme.palette[color]) {
    tonal = {
      ...(variant === 'filled' && { ...styleFilled(color) }),
      ...(variant === 'outlined' && { ...styleOutlined(color) }),
      ...(variant === 'ghost' && { ...styleGhost(color) }),
      ...(variant === 'text' && { ...styleText(color) }),
    }
  }

  // 24px tall, 12.5px text: the smallest pill that still reads comfortably in a
  // data row. Rows around it are content-sized, so nothing is pinned to the old 22.
  return {
    height: 24,
    minWidth: 24,
    lineHeight: 0,
    borderRadius: theme.shape.borderRadius,
    cursor: 'default',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
    color: theme.palette.grey[800],
    fontSize: theme.typography.pxToRem(12.5),
    fontFamily: theme.typography.fontFamily,
    fontVariantNumeric: 'tabular-nums',
    backgroundColor: theme.palette.grey[300],
    fontWeight: 600,

    ...tonal,
  }
})

// ----------------------------------------------------------------------

// `tone` + `scale` pick a clinical colour instead of an MUI palette colour:
//   <Label tone="likelyPathogenic">LP</Label>             (scale defaults to 'acmg')
//   <Label tone="conflicting" scale="clinvar">CI</Label>
// Existing `color=` usage is unchanged; an unknown tone falls back to `color`.
export default function Label({ color = 'default', variant = 'ghost', tone, scale = 'acmg', children, ...other }) {
  return (
    <RootStyle styleProps={{ color, variant, tone, scale }} {...other}>
      {children}
    </RootStyle>
  )
}

Label.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'info', 'success', 'warning', 'error']),
  variant: PropTypes.oneOf(['filled', 'outlined', 'ghost', 'text']),
  tone: PropTypes.string,
  scale: PropTypes.oneOf(SCALES),
}
